import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CreateSmtOrderByOrderIdRequest } from 'src/app/model/api/api-request/create-smt-order-by-order-id-request';
import { CreateSmtOrderRequest } from 'src/app/model/api/api-request/create-smt-order-request';
import { RegisterPaymethodIdByExternalReferenceRequest } from 'src/app/model/api/api-request/order-register-paymethod-id-by-external-reference';
import { SustainOrderRequest } from 'src/app/model/api/api-request/sustain-order-request';
import { UpdateOrderRequest } from 'src/app/model/api/api-request/update-order-request';
import { UpdateSplittestValuePairOnOrderRequest } from 'src/app/model/api/api-request/update-splittest-value-pair-on-order-request';
import { TrackingChannel } from 'src/app/model/enums/tracking-channel';
import { SplittestValuePair } from 'src/app/model/interfaces/splittest-value-pair';
import { TicketInstance } from 'src/app/model/interfaces/ticket-instance';
import { AppConfigService } from 'src/app/services/app-config.service';
import { ClientService } from 'src/app/services/client.service';
import { OrderRequest } from '../../model/api/api-request/order-request';
import { PackageAddReservationRequest } from '../../model/api/api-request/package-add-reservation-request';
import { PackageDeleteReservationRequest } from '../../model/api/api-request/package-delete-reservation-request';
import { ShareAddReservationRequest } from '../../model/api/api-request/share-add-reservation-request';
import { ShareDeleteReservationRequest } from '../../model/api/api-request/share-delete-reservation-request';
import { ShareTypeAddReservationRequest } from '../../model/api/api-request/share-type-add-reservation-request';
import { LotteryClass } from '../../model/interfaces/lottery-class';
import { Order } from '../../model/interfaces/order';
import { Share } from '../../model/interfaces/share';
import { ShareType } from '../../model/interfaces/share-type';
import { TicketPackage } from '../../model/interfaces/ticket-package';
import { TicketPackageInstance } from '../../model/interfaces/ticket-package-instance';
import { ApiClient } from '../api-client/api-client';

@Injectable()
export class OrderRepository
{
    private readonly apiClient: ApiClient;
    private readonly clientService: ClientService;

    public constructor(apiClient: ApiClient, clientService: ClientService, private environment: AppConfigService)
    {
        this.apiClient = apiClient;
        this.clientService = clientService;
    }

    public getOrder(id?: number | null, createIfNull: boolean = true): Observable<Order>
    {
        // get user information from order client info service
        const orderClientInfo = this.clientService.getClientInfo();

        const webapiBasepath = this.environment.config['webapibasepath'];

        let request = null;
        if (id)
        {
            request = new OrderRequest(webapiBasepath, id,  null, null, null, null, null);

        }
        else
        {
            request = new OrderRequest(webapiBasepath, id, orderClientInfo.Referrer, orderClientInfo.Browser, orderClientInfo.Platform, orderClientInfo.Version, orderClientInfo.TrackingChannel, orderClientInfo.TrackingMetadataValuePair);

        }
        return this.apiClient.get(request);
    }

    public createSmtOrder(smtInstances: TicketInstance[]): Observable<Order>
    {
        const orderClientInfo = this.clientService.getClientInfo();
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new CreateSmtOrderRequest(smtInstances, webapiBasepath, orderClientInfo.Referrer, orderClientInfo.Browser, orderClientInfo.Platform, orderClientInfo.Version, orderClientInfo.TrackingChannel);
        return this.apiClient.post(request);
    }

    public createSmtOrderByOrderId(orderId: number): Observable<Order>
    {
        const orderClientInfo = this.clientService.getClientInfo();
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new CreateSmtOrderByOrderIdRequest(orderId, webapiBasepath, orderClientInfo.Referrer, orderClientInfo.Browser, orderClientInfo.Platform, orderClientInfo.Version, orderClientInfo.TrackingChannel);
        return this.apiClient.post(request);
    }

    public addShareType(order: Order, shareType: ShareType, lotteryClass: LotteryClass, count: number): Observable<Order>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new ShareTypeAddReservationRequest(order, shareType, lotteryClass, count, webapiBasepath);
        return this.apiClient.post(request);
    }

    public addShare(order: Order, lotteryClass: LotteryClass, share: Share): Observable<Order>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new ShareAddReservationRequest(order, share, lotteryClass, webapiBasepath);
        return this.apiClient.post(request);
    }

    public deleteShare(order: Order, share: Share): Observable<Order>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new ShareDeleteReservationRequest(order, share, webapiBasepath);
        return this.apiClient.delete(request);
    }

    public addPackage(order: Order, ticketPackage: TicketPackage, count: number): Observable<Order>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new PackageAddReservationRequest(order, ticketPackage, count, webapiBasepath);
        return this.apiClient.post(request);
    }

    public deletePackage(order: Order, ticketPackageInstance: TicketPackageInstance): Observable<Order>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new PackageDeleteReservationRequest(order, ticketPackageInstance, webapiBasepath);
        return this.apiClient.delete(request);
    }

    public updateOrder(order: Order): Observable<Order>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new UpdateOrderRequest(order, webapiBasepath);
        return this.apiClient.put(request);
    }

    public sustainOrderById(orderId: number): Observable<Order>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new SustainOrderRequest(orderId, webapiBasepath);
        return this.apiClient.put(request);
    }

    public updateSplittestValuePairOnOrder(order: Order, splittestValuePair: SplittestValuePair, trackingChannel: TrackingChannel): Observable<Order>
    {
        const webapiBasepath = this.environment.config['webapibasepath'];
        const request = new UpdateSplittestValuePairOnOrderRequest(order, splittestValuePair, trackingChannel, webapiBasepath);
        return this.apiClient.put(request);
    }

    public registerPaymethodIdByExternalReference(orderId: number, paymentMethodExternalReference: string): Observable<void>{
      const webapiBasepath = this.environment.config['webapibasepath'];
      const request = new RegisterPaymethodIdByExternalReferenceRequest(orderId, paymentMethodExternalReference, webapiBasepath);
      return this.apiClient.post(request);
    }
    
}
